import axios from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { useToast } from 'vue-toastification'

// Get API URL from runtime config or fallback to build-time env or same-origin
const getApiBaseUrl = () => {
  // Check if runtime config is available (injected by docker-entrypoint.sh)
  if (typeof window !== 'undefined' && (window as any).ENV?.VITE_API_BASE_URL) {
    return (window as any).ENV.VITE_API_BASE_URL
  }
  // Fallback to build-time environment variable, otherwise use same-origin (empty baseURL)
  return (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '')
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Log the API base URL for debugging
console.log('API Base URL:', getApiBaseUrl())

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`)
    }

    return config
  },
  (error) => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log response in development
    if (import.meta.env.DEV) {
      console.log(`API Response: ${response.status} ${response.config.url}`)
    }
    
    return response
  },
  (error) => {
    const toast = useToast()
    
    if (error.response) {
      // Server responded with error status
      const status = error.response.status
      const message = error.response.data?.detail || error.response.data?.message || 'An error occurred'
      
      switch (status) {
        case 400:
          console.error('Bad Request:', message)
          break
        case 401:
          console.error('Unauthorized:', message)
          // Handle token expiration
          localStorage.removeItem('auth_token')
          break
        case 403:
          console.error('Forbidden:', message)
          toast.error('Access denied')
          break
        case 404:
          console.error('Not Found:', message)
          break
        case 422:
          console.error('Validation Error:', message)
          break
        case 500:
          console.error('Server Error:', message)
          toast.error('Server error occurred')
          break
        default:
          console.error(`HTTP ${status}:`, message)
          toast.error(`Error: ${message}`)
      }
    } else if (error.request) {
      // Network error
      console.error('Network Error:', error.message)
      toast.error('Network error - please check your connection')
    } else {
      // Other error
      console.error('Error:', error.message)
      toast.error('An unexpected error occurred')
    }
    
    return Promise.reject(error)
  }
)

// API service methods
export const api = {
  // Generic methods
  get: <T = any>(url: string, config?: any): Promise<AxiosResponse<T>> =>
    apiClient.get(url, config),

  post: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> =>
    apiClient.post(url, data, config),

  put: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> =>
    apiClient.put(url, data, config),

  patch: <T = any>(url: string, data?: any, config?: any): Promise<AxiosResponse<T>> =>
    apiClient.patch(url, data, config),

  delete: <T = any>(url: string, config?: any): Promise<AxiosResponse<T>> =>
    apiClient.delete(url, config),
  
  // Tickers
  tickers: {
    getAll: (activeOnly: boolean = false) => {
      const params = { active_only: activeOnly }
      return api.get('/api/v1/tickers', { params })
    },
    getById: (id: number) => api.get(`/api/v1/tickers/${id}`),
    create: (data: { symbol: string; name: string; is_active?: boolean }) =>
      api.post('/api/v1/tickers', data),
    update: (id: number, data: Partial<{ symbol: string; name: string; is_active: boolean }>) =>
      api.put(`/api/v1/tickers/${id}`, data),
    delete: (id: number) => api.delete(`/api/v1/tickers/${id}`),
    activate: (id: number) => api.post(`/api/v1/tickers/${id}/activate`),
    deactivate: (id: number) => api.put(`/api/v1/tickers/${id}`, { is_active: false }),
  },
  
  // Prices
  prices: {
    getLatest: (symbols?: string[]) => {
      const params = symbols ? { symbols: symbols.join(',') } : {}
      return api.get('/api/v1/prices/latest', { params })
    },
    getHistory: (symbol: string, days: number = 30) =>
      api.get(`/api/v1/prices/history/${symbol}`, { params: { days } }),
    getCurrent: (symbol: string) => api.get(`/api/v1/prices/current/${symbol}`),
  },
  
  // Alerts
  alerts: {
    getAll: () => api.get('/api/v1/alerts'),
    getById: (id: number) => api.get(`/api/v1/alerts/${id}`),
    create: (data: any) => api.post('/api/v1/alerts', data),
    update: (id: number, data: any) => api.put(`/api/v1/alerts/${id}`, data),
    delete: (id: number) => api.delete(`/api/v1/alerts/${id}`),
    getHistory: (alertId?: number) => {
      const params = alertId ? { alert_id: alertId } : {}
      return api.get('/api/v1/alerts/history', { params })
    },
    // Catalog of named preset strategies (labels, thresholds, notes)
    getStrategyPresets: () => api.get('/api/v1/alerts/strategy-presets'),
  },
  
  // Strategies (legacy)
  strategies: {
    getAll: () => api.get('/api/v1/strategies'),
    getById: (id: number) => api.get(`/api/v1/strategies/${id}`),
    create: (data: any) => api.post('/api/v1/strategies', data),
    update: (id: number, data: any) => api.put(`/api/v1/strategies/${id}`, data),
    delete: (id: number) => api.delete(`/api/v1/strategies/${id}`),
    getSignals: (strategyId?: number) => {
      const params = strategyId ? { strategy_id: strategyId } : {}
      return api.get('/api/v1/strategies/signals', { params })
    },
  },

  // Custom Strategies (new ticker-based system)
  customStrategies: {
    getAll: (activeOnly: boolean = false) =>
      api.get('/api/v1/custom-strategies', { params: { active_only: activeOnly } }),
    getById: (id: number) => api.get(`/api/v1/custom-strategies/${id}`),
    create: (data: {
      name: string
      description?: string
      price_direction: 'FALL' | 'RISE'
      percentage_threshold: number
      time_period_days: number
      action_type: 'BUY' | 'SELL'
      action_value_type: 'SHARES' | 'PERCENTAGE'
      action_value: number
      is_active?: boolean
      ticker_ids?: number[]
    }) => api.post('/api/v1/custom-strategies', data),
    update: (id: number, data: Partial<{
      name: string
      description: string
      price_direction: 'FALL' | 'RISE'
      percentage_threshold: number
      time_period_days: number
      action_type: 'BUY' | 'SELL'
      action_value_type: 'SHARES' | 'PERCENTAGE'
      action_value: number
      is_active: boolean
    }>) => api.put(`/api/v1/custom-strategies/${id}`, data),
    delete: (id: number) => api.delete(`/api/v1/custom-strategies/${id}`),

    // Ticker attachment
    attachTickers: (strategyId: number, tickerIds: number[]) =>
      api.post(`/api/v1/custom-strategies/${strategyId}/tickers`, { ticker_ids: tickerIds }),
    detachTickers: (strategyId: number, tickerIds: number[]) =>
      api.delete(`/api/v1/custom-strategies/${strategyId}/tickers`, { data: { ticker_ids: tickerIds } }),

    // Ticker overrides
    setTickerOverride: (strategyId: number, tickerId: number, actionValue: number, actionValueType: 'SHARES' | 'PERCENTAGE') =>
      api.put(`/api/v1/custom-strategies/${strategyId}/tickers/${tickerId}/override`, {
        ticker_id: tickerId,
        action_value: actionValue,
        action_value_type: actionValueType
      }),
    clearTickerOverride: (strategyId: number, tickerId: number) =>
      api.delete(`/api/v1/custom-strategies/${strategyId}/tickers/${tickerId}/override`),
  },

  // API Keys
  apiKeys: {
    getAll: () => api.get('/api/v1/api-keys'),
    getById: (id: number) => api.get(`/api/v1/api-keys/${id}`),
    create: (data: { title: string }) => api.post('/api/v1/api-keys', data),
    update: (id: number, data: { title?: string; is_active?: boolean }) =>
      api.put(`/api/v1/api-keys/${id}`, data),
    delete: (id: number) => api.delete(`/api/v1/api-keys/${id}`),
  },

  // Settings
  settings: {
    get: () => api.get('/api/v1/settings'),
    update: (data: any) => api.patch('/api/v1/settings', data),
  },

  // Runtime app config (active broker, exchange, etc.)
  config: {
    get: () => api.get('/api/v1/config/'),
  },

  // Broker-agnostic manual trading (works with the active BROKER)
  broker: {
    getAccounts: () => api.get('/api/v1/broker/accounts'),
    getPortfolio: (accountIdKey?: string) =>
      api.get('/api/v1/broker/portfolio', { params: { account_id_key: accountIdKey } }),
    getOrders: (status: string = 'OPEN', accountIdKey?: string) =>
      api.get('/api/v1/broker/orders', { params: { status, account_id_key: accountIdKey } }),
    previewOrder: (data: {
      symbol: string
      order_action: string
      quantity: number
      price_type: string
      limit_price?: number | null
      order_term?: string
      market_session?: string
      account_id_key?: string
    }) => api.post('/api/v1/broker/orders/preview', data),
    placeOrder: (data: {
      symbol: string
      order_action: string
      quantity: number
      price_type: string
      limit_price?: number | null
      order_term?: string
      market_session?: string
      account_id_key?: string
      preview_id?: number | null
      client_order_id?: string | null
      // Reserve the bought shares from Alerts-page strategy sells
      hold_from_strategy?: boolean
      // Queue the order for the next regular-session open instead of now
      schedule_at_market_open?: boolean
    }) => api.post('/api/v1/broker/orders/place', data),
    cancelOrder: (orderId: number, accountIdKey?: string) =>
      api.post('/api/v1/broker/orders/cancel', { order_id: orderId, account_id_key: accountIdKey }),
    // Hold a ticker from strategy sells until it is released
    setPositionHold: (symbol: string, hold: boolean) =>
      api.post('/api/v1/broker/positions/hold', { symbol, hold }),
    getPositionHolds: () => api.get('/api/v1/broker/positions/holds'),
  },
  
  // Market
  market: {
    getStatus: () => api.get('/api/v1/market/status'),
    getQuoteRaw: (symbol: string, provider?: 'alpaca' | 'etrade' | 'ibkr') =>
      api.get('/api/v1/market/quote-raw', { params: { symbol, provider } }),
  },

  // Orders
  orders: {
    getAll: (status?: string, tickerId?: number) => {
      const params: any = {}
      if (status) params.status = status
      if (tickerId) params.ticker_id = tickerId
      return api.get('/api/v1/orders/', { params })
    },
    getById: (id: number) => api.get(`/api/v1/orders/${id}`),
    create: (data: { ticker_id: number; order_type: string; quantity: number; price: number; reason?: string }) =>
      api.post('/api/v1/orders/', data),
    cancel: (id: number) => api.post(`/api/v1/orders/${id}/cancel`),
    getStats: () => api.get('/api/v1/orders/stats/summary'),
    syncFromEtrade: () => api.post('/api/v1/orders/sync-from-etrade'),
    syncFromIbkr: () => api.post('/api/v1/orders/sync-from-ibkr'),
    getEtradeDetails: (id: number) => api.get(`/api/v1/orders/${id}/etrade-details`),
  },

  // Interactive Brokers (IBKR) gateway authentication
  ibkr: {
    // Gateway auth status + whether it is currently waiting for a 2FA code
    getAuthStatus: () => api.get('/api/v1/ibkr/auth/status'),
    // Submit a manually-entered IBKR 2FA / OTP code for the gateway to consume
    submitOtp: (code: string) => api.post('/api/v1/ibkr/auth/otp', { code }),
    // Pause / resume the gateway's login loop (stop it re-logging-in every ~60s)
    pauseLogin: () => api.post('/api/v1/ibkr/auth/pause-login'),
    resumeLogin: () => api.post('/api/v1/ibkr/auth/resume-login'),
    // Restart the gateway container when it goes stale. No arguments - the
    // backend restarts one fixed container and accepts no command from here.
    restartGateway: () => api.post('/api/v1/ibkr/auth/restart-gateway'),
  },

  // Positions
  positions: {
    getAll: (includeClosed: boolean = false) => {
      const params = { include_closed: includeClosed }
      return api.get('/api/v1/positions/', { params })
    },
    getByTicker: (tickerId: number) => api.get(`/api/v1/positions/${tickerId}`),
    updatePnL: () => api.post('/api/v1/positions/update-pnl'),
    getStats: () => api.get('/api/v1/positions/stats/summary'),
    syncFromEtrade: (accountId?: string) =>
      api.post('/api/v1/positions/sync-from-etrade', null, { params: { account_id: accountId } }),
  },

  // E*TRADE Authentication
  etrade: {
    // Auth endpoints
    initiateOAuth: (isSandbox: boolean = true) =>
      api.post('/api/v1/etrade/auth/initiate', { is_sandbox: isSandbox }),
    completeOAuth: (verifierCode: string, isSandbox: boolean = true) =>
      api.post('/api/v1/etrade/auth/complete', { verifier_code: verifierCode, is_sandbox: isSandbox }),
    renewToken: (isSandbox: boolean = true) =>
      api.post('/api/v1/etrade/auth/renew', null, { params: { is_sandbox: isSandbox } }),
    getStatus: () => api.get('/api/v1/etrade/auth/status'),
    switchEnvironment: (isSandbox: boolean) =>
      api.post('/api/v1/etrade/auth/switch-environment', { is_sandbox: isSandbox }),
    revokeCredentials: (isSandbox: boolean = true) =>
      api.delete('/api/v1/etrade/auth/revoke', { params: { is_sandbox: isSandbox } }),
    setDummyTrading: (isSandbox: boolean, dummyTrading: boolean) =>
      api.post('/api/v1/etrade/auth/dummy-trading', { is_sandbox: isSandbox, dummy_trading: dummyTrading }),

    // Account endpoints
    getAccounts: () => api.get('/api/v1/etrade/accounts'),
    getPortfolio: (accountIdKey: string) => api.get(`/api/v1/etrade/portfolio/${accountIdKey}`),
    getBalance: (accountIdKey: string, institutionType: string = 'BROKERAGE') =>
      api.get(`/api/v1/etrade/balance/${accountIdKey}`, { params: { institution_type: institutionType } }),

    // Order endpoints
    previewOrder: (data: {
      account_id_key: string
      symbol: string
      order_action: string
      quantity: number
      price_type: string
      limit_price?: number
      order_term?: string
      market_session?: string
    }) => api.post('/api/v1/etrade/orders/preview', data),

    placeOrder: (data: {
      account_id_key: string
      preview_id: number
      symbol: string
      order_action: string
      quantity: number
      price_type: string
      limit_price?: number
      order_term?: string
      client_order_id: string
      market_session?: string
    }) => api.post('/api/v1/etrade/orders/place', data),

    getOrders: (accountIdKey: string, status: string = 'OPEN') =>
      api.get(`/api/v1/etrade/orders/${accountIdKey}`, { params: { status } }),

    cancelOrder: (data: { account_id_key: string; order_id: number }) =>
      api.post('/api/v1/etrade/orders/cancel', data),

    placeOrderFromAlert: (data: {
      alert_id: number
      account_id_key: string
      quantity: number
      price_type?: string
      limit_price?: number
    }) => api.post('/api/v1/etrade/orders/place-from-alert', data),
  },

  // Health check
  health: () => api.get('/health'),
}

// Export the axios instance for direct use if needed
export { apiClient }

export default api
